'use strict';

import { useReducer, useEffect, useCallback, useRef } from 'react';
import moment from 'moment';
import {
    getCurrentActiveMealType,
    setStoredMealType,
    clearStoredMealType,
    getStoredMealType,
    getNextMealType,
    getPreviousMealType,
    isValidMealType,
    getFallbackMealType,
    STORE_KEY
} from '../utils/MealTypeCalculator';

// Action types for reducer
const ACTIONS = {
    SET_MEAL_TYPE: 'SET_MEAL_TYPE',
    CLEAR_MANUAL_SELECTION: 'CLEAR_MANUAL_SELECTION',
    UPDATE_FROM_TIMER: 'UPDATE_FROM_TIMER',
    SYNC_FROM_STORE: 'SYNC_FROM_STORE',
    SET_NAVIGATION_MEAL: 'SET_NAVIGATION_MEAL',
    RESET_NAVIGATION: 'RESET_NAVIGATION',
    UPDATE_PREFERENCES: 'UPDATE_PREFERENCES',  // New action for preferences updates
    CLEAR_NAVIGATE_TO_TODAY: 'CLEAR_NAVIGATE_TO_TODAY'  // Clear the navigate to today flag
};

/**
 * Initial state for meal type manager
 */
function getInitialState(preferences, plans) {
    // Initial state for meal type manager

    // If no preferences yet, return a loading/pending state
    if (!preferences || !preferences.meal_types || preferences.meal_types.length === 0) {
        return {
            currentMealType: null,       // Will be calculated once data loads
            activeMealType: null,        // Will be calculated once data loads
            isManualSelection: false,
            isNavigating: false,
            lastUpdated: Date.now(),
            timerInterval: null,
            isLoading: true,            // New loading flag
            shouldNavigateToToday: false // Flag to trigger navigation to today
        };
    }

    const currentMealType = getCurrentActiveMealType(preferences, plans);

    // Check if there's a stored meal type to determine if it's manual selection
    const mealTypes = preferences?.meal_types || [];
    const storedMealType = getStoredMealType(mealTypes);
    const isManualSelection = !!storedMealType;

    return {
        currentMealType,           // Global/temporal meal type
        activeMealType: currentMealType,  // Local navigation meal type
        isManualSelection,         // TRUE if there's a stored selection
        isNavigating: false,       // User is navigating independently
        lastUpdated: Date.now(),   // For cache invalidation
        timerInterval: null,       // Timer reference
        isLoading: false,         // Data is loaded
        shouldNavigateToToday: false  // Flag to trigger navigation to today
    };
}

/**
 * Reducer for atomic state updates
 */
function mealTypeReducer(state, action) {

    switch (action.type) {
        case ACTIONS.UPDATE_PREFERENCES:
            // Handle initial data load
            if (state.isLoading && action.payload.preferences?.meal_types?.length > 0) {
                const currentMealType = getCurrentActiveMealType(
                    action.payload.preferences,
                    action.payload.plans
                );

                // Check if there's a stored meal type to determine if it's manual selection
                const mealTypes = action.payload.preferences?.meal_types || [];
                const storedMealType = getStoredMealType(mealTypes);
                const isManualSelection = !!storedMealType;

                return {
                    ...state,
                    currentMealType,
                    activeMealType: currentMealType,
                    isManualSelection,  // Preserve manual selection state
                    isLoading: false,
                    lastUpdated: Date.now()
                };
            }

            return state;

        case ACTIONS.SET_MEAL_TYPE:
            const newState = {
                ...state,
                currentMealType: action.payload.mealType,
                activeMealType: action.payload.mealType, // Sync navigation
                isManualSelection: action.payload.manual,
                isNavigating: false, // Reset navigation when setting global
                lastUpdated: Date.now()
            };

            return newState;

        case ACTIONS.CLEAR_MANUAL_SELECTION:
            return {
                ...state,
                isManualSelection: false,
                currentMealType: action.payload.newMealType,
                activeMealType: action.payload.newMealType,
                isNavigating: false,
                lastUpdated: Date.now()
            };

        case ACTIONS.UPDATE_FROM_TIMER:
            // Only update if no manual selection
            if (state.isManualSelection) {
                return state;
            }

            return {
                ...state,
                currentMealType: action.payload.mealType,
                // Don't update activeMealType if user is navigating
                activeMealType: state.isNavigating ? state.activeMealType : action.payload.mealType,
                lastUpdated: Date.now()
            };

        case ACTIONS.SYNC_FROM_STORE:
            // Handle store changes from other tabs/windows
            if (action.payload.storedValue) {
                return {
                    ...state,
                    currentMealType: action.payload.storedValue,
                    activeMealType: action.payload.storedValue,
                    isManualSelection: true,
                    isNavigating: false,
                    lastUpdated: Date.now()
                };
            } else {
                // Store was cleared
                return {
                    ...state,
                    isManualSelection: false,
                    currentMealType: action.payload.fallbackMealType,
                    activeMealType: action.payload.fallbackMealType,
                    isNavigating: false,
                    lastUpdated: Date.now()
                };
            }

        case ACTIONS.SET_NAVIGATION_MEAL:
            return {
                ...state,
                activeMealType: action.payload.mealType,
                isNavigating: true
            };

        case ACTIONS.RESET_NAVIGATION:
            return {
                ...state,
                activeMealType: state.currentMealType,
                isNavigating: false,
                shouldNavigateToToday: action.payload?.navigateToToday || false  // Flag to signal navigation to today
            };

        case ACTIONS.CLEAR_NAVIGATE_TO_TODAY:
            return {
                ...state,
                shouldNavigateToToday: false
            };

        default:
            return state;
    }
}

/**
 * Custom hook for managing meal type state
 */
export default function useMealTypeManager(config = {}) {
    const {
        preferences,
        plans,
        autoTimer = true,
        timerInterval = 15 * 60 * 1000, // 15 minutes
        onMealTypeChange,
    } = config;

    // Initialize state
    const [state, dispatch] = useReducer(
        mealTypeReducer,
        { preferences, plans },
        getInitialState
    );

    // Refs for stable references
    const onMealTypeChangeRef = useRef(onMealTypeChange);
    const preferencesRef = useRef(preferences);
    const plansRef = useRef(plans);

    // Update refs when props change
    useEffect(() => {
        onMealTypeChangeRef.current = onMealTypeChange;
        preferencesRef.current = preferences;
        plansRef.current = plans;
    });

    // Handle preferences updates and initial load
    useEffect(() => {

        // If we're in loading state and preferences are now available
        if (state.isLoading && preferences?.meal_types?.length > 0) {
            dispatch({
                type: ACTIONS.UPDATE_PREFERENCES,
                payload: { preferences, plans }
            });
        }
    }, [preferences, plans, state.isLoading]);

    // Timer for automatic meal type updates
    useEffect(() => {
        if (!autoTimer) return;

        const intervalId = setInterval(() => {
            const newMealType = getCurrentActiveMealType(
                preferencesRef.current,
                plansRef.current
            );

            if (newMealType !== state.currentMealType) {
                dispatch({
                    type: ACTIONS.UPDATE_FROM_TIMER,
                    payload: { mealType: newMealType }
                });
            }
        }, timerInterval);

        return () => {
            clearInterval(intervalId);
        };
    }, [autoTimer, timerInterval, state.currentMealType]);

    // Listen for storage changes (cross-tab synchronization)
    useEffect(() => {
        const handleStorageChange = (event) => {
            if (event.key === STORE_KEY) {
                const fallbackMealType = getCurrentActiveMealType(
                    preferencesRef.current,
                    plansRef.current
                );

                dispatch({
                    type: ACTIONS.SYNC_FROM_STORE,
                    payload: {
                        storedValue: event.newValue,
                        fallbackMealType
                    }
                });
            }
        };

        window.addEventListener('storage', handleStorageChange);
        return () => window.removeEventListener('storage', handleStorageChange);
    }, []);

    // This useEffect will be moved after setMealType is defined

    // Actions
    const setMealType = useCallback((mealType, manual = true) => {
        if (manual) {
            setStoredMealType(mealType);
        }

        dispatch({
            type: ACTIONS.SET_MEAL_TYPE,
            payload: { mealType, manual }
        });
    }, []);

    // Notify parent component when meal type changes
    // Now we can pass setMealType as the third parameter
    useEffect(() => {
        if (onMealTypeChangeRef.current) {
            onMealTypeChangeRef.current(state.currentMealType, state.isManualSelection, setMealType);
        }
    }, [state.currentMealType, state.isManualSelection, setMealType]);

    const clearManualSelection = useCallback(() => {
        clearStoredMealType();

        const newMealType = getCurrentActiveMealType(
            preferencesRef.current,
            plansRef.current
        );

        dispatch({
            type: ACTIONS.CLEAR_MANUAL_SELECTION,
            payload: { newMealType }
        });
    }, []);

    const navigateToMealType = useCallback((mealType) => {
        dispatch({
            type: ACTIONS.SET_NAVIGATION_MEAL,
            payload: { mealType }
        });
    }, []);

    const navigateNext = useCallback(() => {
        const mealTypes = preferences?.meal_types || [];
        const nextMealType = getNextMealType(state.activeMealType, mealTypes);

        if (nextMealType) {
            navigateToMealType(nextMealType);
        }

        return nextMealType;
    }, [state.activeMealType, preferences, navigateToMealType]);

    const navigatePrevious = useCallback(() => {
        const mealTypes = preferences?.meal_types || [];
        const prevMealType = getPreviousMealType(state.activeMealType, mealTypes);

        if (prevMealType) {
            navigateToMealType(prevMealType);
        }

        return prevMealType;
    }, [state.activeMealType, preferences, navigateToMealType]);

    const syncNavigationToCurrent = useCallback(() => {
        dispatch({
            type: ACTIONS.RESET_NAVIGATION,
            payload: {
                navigateToToday: true  // Signal that we should navigate to today's date
            }
        });
    }, []);

    const clearNavigateToToday = useCallback(() => {
        dispatch({
            type: ACTIONS.CLEAR_NAVIGATE_TO_TODAY
        });
    }, []);

    const canNavigateNext = useCallback(() => {
        const mealTypes = preferences?.meal_types || [];
        return getNextMealType(state.activeMealType, mealTypes) !== null;
    }, [state.activeMealType, preferences]);

    const canNavigatePrevious = useCallback(() => {
        const mealTypes = preferences?.meal_types || [];
        return getPreviousMealType(state.activeMealType, mealTypes) !== null;
    }, [state.activeMealType, preferences]);

    // Validate current state against available meal types when preferences change
    const prevMealTypesRef = useRef();
    useEffect(() => {
        const mealTypes = preferences?.meal_types || [];
        const prevMealTypes = prevMealTypesRef.current;
        // Skip validation if still loading (preferences update will handle it)
        if (state.isLoading) {
            return;
        }

        // Only validate if meal types actually changed
        if (prevMealTypes !== mealTypes) {
            prevMealTypesRef.current = mealTypes;

            // Skip validation if currentMealType is null and this is the initial mount
            // This can happen when preferences load before the UPDATE_PREFERENCES action completes
            if (state.currentMealType === null && !prevMealTypes) {
                return;
            }

            const isCurrentValid = isValidMealType(state.currentMealType, mealTypes);

            if (!isCurrentValid) {
                const fallbackMealType = getFallbackMealType(mealTypes);

                // Clear manual selection if meal type no longer exists
                if (state.isManualSelection) {
                    clearStoredMealType();
                }
                dispatch({
                    type: ACTIONS.SET_MEAL_TYPE,
                    payload: { mealType: fallbackMealType, manual: false }
                });
            }
        }
    }, [preferences?.meal_types, state.currentMealType, state.isManualSelection]);

    return {
        // State
        currentMealType: state.currentMealType,
        activeMealType: state.activeMealType,
        isManualSelection: state.isManualSelection,
        isNavigating: state.isNavigating,
        isLoading: state.isLoading,  // Add loading state
        shouldNavigateToToday: state.shouldNavigateToToday, // Flag for navigating to today
        lastUpdated: state.lastUpdated,

        // Actions
        setMealType,
        clearManualSelection,
        navigateToMealType,
        navigateNext,
        navigatePrevious,
        syncNavigationToCurrent,
        clearNavigateToToday,

        // Navigation helpers
        canNavigateNext: canNavigateNext(),
        canNavigatePrevious: canNavigatePrevious(),
    };
}